import { JwtTokenFullData } from 'multirpc-sdk';
import { createSelector } from 'reselect';
import { t } from '@ankr.com/common';

import { MultiService } from 'modules/api/MultiService';
import { NotificationActions } from 'domains/notification/store/NotificationActions';
import { TFAEndpointMeta, TFAQueryFnParams } from 'modules/tfa/types';
import { createQueryFnWithErrorHandler } from 'store/utils/createQueryFnWithErrorHandler';
import { createQuerySelectors } from 'store/utils/createQuerySelectors';
import { decryptJWT } from 'modules/common/utils/decryptJWT';
import { isTFAError } from 'modules/tfa/utils/isTFAError';
import { signOut } from 'modules/auth/actions/signOut';
import { web3Api } from 'store/queries';

import { isEmptyEthAddressAuthError } from './utils/isEmptyEthAddressAuthError';

const { showNotification } = NotificationActions;

export interface IFetchStandardAccountJWTProps
  extends TFAQueryFnParams<unknown> {}

// The endpoint name is listed in endpointsSerializedByParams constant
// in packages/protocol/src/store/queries/index.ts file.
// If the name has changed it should be reflected there as well.
export const {
  endpoints: { fetchStandardAccountJWT },
  useFetchStandardAccountJWTQuery,
  useLazyFetchStandardAccountJWTQuery,
} = web3Api.injectEndpoints({
  endpoints: build => ({
    fetchStandardAccountJWT: build.query<
      JwtTokenFullData,
      IFetchStandardAccountJWTProps
    >({
      queryFn: createQueryFnWithErrorHandler({
        queryFn: async ({ totp }) => {
          const api = MultiService.getService().getAccountingGateway();

          const { is_encrypted: isEncrypted, jwt_data: jwt } =
            await api.getOrCreateInstantJwt(totp);

          const decryptedJWT = await decryptJWT({ isEncrypted, jwt });

          return { data: decryptedJWT };
        },
        errorHandler: (error, _args, { dispatch }) => {
          if (isEmptyEthAddressAuthError(error)) {
            dispatch(
              showNotification({
                autoHideDuration: null,
                message: t('error.empty-eth'),
                severity: 'error',
              }),
            );

            dispatch(signOut.initiate());
          }

          const meta: TFAEndpointMeta = {
            shouldSignOutOnTFAError: true,
          };

          return { error, meta };
        },
      }),
    }),
  }),
  overrideExisting: true,
});

export const fetchStandardAccountJWTFallback: JwtTokenFullData = {};

export const {
  selectDataWithFallbackCachedByParams: selectStandardAccountJWT,
  selectStateCachedByParams: selectStandardAccountJWTState,
} = createQuerySelectors({
  endpoint: fetchStandardAccountJWT,
  fallback: fetchStandardAccountJWTFallback,
});

export const selectStandardAccountJWTLoading = createSelector(
  selectStandardAccountJWTState,
  ({ error, isLoading }) => isLoading || isTFAError(error),
);
